import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Mail, MapPin } from 'lucide-react';

const BRAND = '#004276';

function Block({ block }) {
  if (block.type === 'p') {
    return <p className="leading-relaxed">{block.text}</p>;
  }
  if (block.type === 'h4') {
    return <h4 className="font-semibold text-gray-900 pt-1">{block.text}</h4>;
  }
  if (block.type === 'ul') {
    return (
      <ul className="list-disc pl-5 space-y-1.5">
        {block.items.map((item, i) => (
          <li key={i} className="leading-relaxed">{item}</li>
        ))}
      </ul>
    );
  }
  if (block.type === 'contact') {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-1.5 text-sm">
        {block.name && <p className="font-semibold text-gray-900">{block.name}</p>}
        {block.address && (
          <p className="flex items-start gap-2 text-gray-600">
            <MapPin size={16} className="mt-0.5 shrink-0" style={{ color: BRAND }} />
            {block.address}
          </p>
        )}
        {block.email && (
          <p className="flex items-center gap-2 text-gray-600">
            <Mail size={16} className="shrink-0" style={{ color: BRAND }} />
            <a href={`mailto:${block.email}`} className="hover:underline" style={{ color: BRAND }}>
              {block.email}
            </a>
          </p>
        )}
      </div>
    );
  }
  return null;
}

function LegalPageLayout({ badge, title, description, warning, intro, acceptance, sections }) {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    );

    sections.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = sectionRefs.current[id];
    if (el) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 88, behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-white">
      {/* Hero */}
      <div className="relative overflow-hidden text-white">
        <div className="absolute inset-0 bg-primary-gradient-135" />
        <div className="absolute inset-0 bg-white/25" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <span className="inline-block text-xs font-semibold uppercase tracking-wider bg-white/20 rounded px-2.5 py-1 mb-4">
            {badge}
          </span>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{title}</h1>
          <p className="text-white/90 max-w-2xl text-sm md:text-base leading-relaxed">{description}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Warning banner */}
        <div className="mt-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-500" />
          <p className="text-xs md:text-sm font-medium leading-relaxed">{warning}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10 py-8 md:py-10">
          {/* Sidebar TOC */}
          <nav className="hidden lg:block">
            <div className="sticky top-24 max-h-[75vh] overflow-y-auto rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 px-2">Contents</p>
              <ul className="space-y-0.5">
                {sections.map((s, i) => {
                  const isActive = activeId === s.id;
                  return (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        onClick={(e) => handleTocClick(e, s.id)}
                        className="block rounded-md px-2 py-1.5 text-sm transition-colors"
                        style={
                          isActive
                            ? { backgroundColor: `${BRAND}14`, color: BRAND, fontWeight: 600 }
                            : { color: '#4b5563' }
                        }
                      >
                        {i + 1}. {s.title}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>

          {/* Content */}
          <div className="text-gray-700 text-sm md:text-[15px]">
            <div className="space-y-4 mb-8">
              {intro.map((text, i) => (
                <p key={i} className="leading-relaxed">{text}</p>
              ))}
            </div>

            {acceptance && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 text-sm font-medium text-gray-800 mb-10">
                {acceptance}
              </div>
            )}

            {sections.map((s, i) => (
              <section
                key={s.id}
                id={s.id}
                ref={(el) => (sectionRefs.current[s.id] = el)}
                className="mb-10 scroll-mt-24"
              >
                <h2 className="flex items-center gap-3 text-lg md:text-xl font-bold text-gray-900 mb-4">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-gradient text-white text-sm font-semibold">
                    {i + 1}
                  </span>
                  {s.title}
                </h2>
                <div className="space-y-3 pl-10">
                  {s.blocks.map((block, bi) => (
                    <Block key={bi} block={block} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LegalPageLayout;
